import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const STORAGE_KEY = "pwa-install-dismissed-v1";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    // Android / desktop Chrome
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari — show after a delay
    if (isIOS()) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {}
      setDeferredPrompt(null);
      setShow(false);
      sessionStorage.setItem(STORAGE_KEY, "1");
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    sessionStorage.setItem(STORAGE_KEY, "1");
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed left-4 right-4 bottom-24 z-[60] mx-auto max-w-md glass rounded-2xl p-4 flex items-center gap-3 animate-in slide-in-from-bottom-4"
    >
      <div className="size-10 rounded-xl bg-[#007AFF]/15 grid place-items-center shrink-0">
        <Download className="text-[#007AFF]" size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">Install Proximity</div>
        <div className="text-xs text-zinc-400">
          {iosHint
            ? "Tap Share, then Add to Home Screen"
            : isIOS()
            ? "Add to your home screen for the full app experience"
            : "Add to your home screen — works offline, sends alerts faster"}
        </div>
        {iosHint && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
            <Share size={12} className="text-[#007AFF]" /> →
            <Plus size={12} className="text-[#007AFF]" /> Add to Home Screen
          </div>
        )}
      </div>
      {!iosHint && (
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="text-xs font-bold bg-[#007AFF] hover:bg-[#005ec4] px-3 py-2 rounded-full text-white shrink-0"
        >
          {isIOS() ? "How" : "Install"}
        </button>
      )}
      <button
        data-testid="pwa-dismiss-btn"
        onClick={dismiss}
        className="size-7 rounded-full border border-white/10 grid place-items-center text-zinc-400 hover:text-zinc-100 shrink-0"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
